/**
 * Calendar dates as `YYYY-MM-DD` strings. They name a day on a wall calendar,
 * not a moment, so every calculation runs on UTC midnights and never on the
 * viewer's own zone. Two keys compare correctly as plain strings.
 */

export type DateKey = string;

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const MONTH_SHORT = MONTH_NAMES.map((m) => m.slice(0, 3));
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_MS = 86_400_000;

const pad = (n: number) => String(n).padStart(2, '0');

export interface DateKeyParts {
  year: number;
  /** 0–11, as `Date` counts months. */
  month: number;
  day: number;
}

export function dateKeyParts(key: DateKey): DateKeyParts {
  const [year, month, day] = key.split('-').map(Number);
  return { year, month: month - 1, day };
}

export function toDateKey(year: number, month: number, day: number): DateKey {
  const d = new Date(Date.UTC(year, month, day));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function utcMs(key: DateKey): number {
  const { year, month, day } = dateKeyParts(key);
  return Date.UTC(year, month, day);
}

export function addDays(key: DateKey, days: number): DateKey {
  const { year, month, day } = dateKeyParts(key);
  return toDateKey(year, month, day + days);
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function daysBetween(from: DateKey, to: DateKey): number {
  return Math.round((utcMs(to) - utcMs(from)) / DAY_MS);
}

/** 0 = Sunday. */
export function weekdayOf(key: DateKey): number {
  return new Date(utcMs(key)).getUTCDay();
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

/** One month laid out Sunday-first, with nulls for the blank cells before the 1st. */
export function monthGrid(year: number, month: number): (DateKey | null)[] {
  const lead: null[] = Array.from({ length: weekdayOf(toDateKey(year, month, 1)) }, () => null);
  const days = Array.from({ length: daysInMonth(year, month) }, (_, i) =>
    toDateKey(year, month, i + 1),
  );
  return [...lead, ...days];
}

/** "Mon, Oct 12, 2026", or "Mon, Oct 12" without the year. */
export function formatDateKey(key: DateKey, { year = true }: { year?: boolean } = {}): string {
  const p = dateKeyParts(key);
  const base = `${WEEKDAY_SHORT[weekdayOf(key)]}, ${MONTH_SHORT[p.month]} ${p.day}`;
  return year ? `${base}, ${p.year}` : base;
}
